import {
  PUBLIC_I18N_INVARIANTS,
  QUAOM_DEFAULT_UI_LANGUAGE,
  QUAOM_SUPPORTED_UI_LANGUAGES,
  assertCompletePublicI18nBundle,
  resolvePublicI18n,
  resolveQuaomUiLanguage,
  type PublicI18nBundle,
  type QuaomContentLanguage,
  type QuaomUiLanguage,
} from '@quaom/contracts/i18n';

export type PublicLanguageContext = {
  uiLanguage: QuaomUiLanguage;
  routeLanguage: QuaomUiLanguage;
  contentLanguage: QuaomContentLanguage | null;
  fallbackLanguage: typeof QUAOM_DEFAULT_UI_LANGUAGE;
  requestedLanguage: string | null;
  usedFallback: boolean;
  uiLanguageDiffersFromContentLanguage: boolean;
};

export type PublicTranslator<TKeys extends string> = {
  lang: QuaomUiLanguage;
  namespace: PublicI18nBundle<TKeys>['namespace'];
  t(key: TKeys): string;
};

export function createPublicLanguageContext(input: {
  routeLang?: string | null;
  runtimeDefaultLang?: string | null;
  contentLang?: string | null;
}): PublicLanguageContext {
  const requestedLanguage =
    input.routeLang || input.runtimeDefaultLang || QUAOM_DEFAULT_UI_LANGUAGE;
  const resolved = resolvePublicI18n({
    requestedLanguage,
    contentLanguage: input.contentLang || null,
  });
  const routeLanguage = resolveQuaomUiLanguage(input.routeLang || resolved.uiLanguage);
  const contentLanguage = input.contentLang || null;
  return {
    uiLanguage: resolved.uiLanguage,
    routeLanguage,
    contentLanguage,
    fallbackLanguage: resolved.fallbackLanguage,
    requestedLanguage: resolved.requestedLanguage,
    usedFallback: resolved.usedFallback,
    uiLanguageDiffersFromContentLanguage: Boolean(
      contentLanguage && contentLanguage !== resolved.uiLanguage,
    ),
  };
}

export function createPublicTranslator<TKeys extends string>(
  bundle: PublicI18nBundle<TKeys>,
  lang: string | null | undefined,
): PublicTranslator<TKeys> {
  assertCompletePublicI18nBundle(bundle);
  const resolved = resolveQuaomUiLanguage(lang);
  const fallback = bundle.messages[QUAOM_DEFAULT_UI_LANGUAGE];
  const messages = bundle.messages[resolved] || fallback;
  return {
    lang: resolved,
    namespace: bundle.namespace,
    t(key: TKeys): string {
      return messages[key] || fallback[key] || key;
    },
  };
}

export function getSupportedPublicUiLanguages(): readonly QuaomUiLanguage[] {
  return QUAOM_SUPPORTED_UI_LANGUAGES;
}

export function getPublicI18nInvariants(): typeof PUBLIC_I18N_INVARIANTS {
  return PUBLIC_I18N_INVARIANTS;
}
